import { useState } from "react";

import { apiBase } from "../game/api";

interface Props {
  sessionId: string | null;
  turn: number;
  onClose: () => void;
}

type Status = "idle" | "submitting" | "success" | "error";

const MIN_LEN = 5;
const MAX_LEN = 2000;

export default function ReportProblemModal({ sessionId, turn, onClose }: Props) {
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const trimmed = description.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_LEN;
  const tooLong = trimmed.length > MAX_LEN;
  const canSubmit = trimmed.length >= MIN_LEN && trimmed.length <= MAX_LEN && status !== "submitting";

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setStatus("submitting");
    setErrorMsg("");
    try {
      const resp = await fetch(`${apiBase()}/api/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          session_id: sessionId,
          turn,
          description: trimmed,
        }),
      });
      if (!resp.ok) {
        let detail = `HTTP ${resp.status}`;
        try {
          const err = await resp.json();
          if (err && typeof err === "object" && "detail" in err) {
            detail = String((err as { detail?: unknown }).detail) || detail;
          }
        } catch {
          /* ignore */
        }
        setErrorMsg(detail);
        setStatus("error");
        return;
      }
      setStatus("success");
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Network error");
      setStatus("error");
    }
  };

  return (
    <div className="report-modal" role="dialog" aria-modal="true">
      <div className="report-modal-content">
        {status === "success" ? (
          <>
            <p>Thanks for the feedback. We logged it.</p>
            <div className="report-modal-actions">
              <button className="report-btn report-confirm" onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <p>Report a problem with this turn:</p>
            <textarea
              className="report-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What went wrong? (5-2000 characters)"
              rows={5}
              autoFocus
              disabled={status === "submitting"}
            />
            <div className="report-modal-meta">
              <span>{trimmed.length}/{MAX_LEN}</span>
              {tooShort && <span className="report-warn">Too short (min {MIN_LEN}).</span>}
              {tooLong && <span className="report-warn">Too long (max {MAX_LEN}).</span>}
              {status === "error" && <span className="report-warn">{errorMsg}</span>}
            </div>
            <div className="report-modal-actions">
              <button
                className="report-btn report-cancel"
                onClick={onClose}
                disabled={status === "submitting"}
              >
                Cancel
              </button>
              <button
                className="report-btn report-confirm"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {status === "submitting" ? "Sending..." : "Submit"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
